/**
 * The block registry: every block type the editor can place, compile and
 * inspect.
 *
 * Core components (`core/*`) and partner fragments are the *same kind of thing*
 * here — a Twig source plus a manifest — so they share one emitter, one
 * inspector and one preview path. Only provenance and editability differ, and
 * both come from the single fragment catalog `GET /template-fragments` already
 * returns (partner rows overriding global ones by key).
 *
 * The registry is therefore **derived from data, not hard-coded**: adding a
 * component means shipping a `.twig` file, not editing TypeScript. Only the two
 * primitives below have TypeScript emitters.
 */
import { NATIVE_BLOCK } from './document';
import {
  ComponentManifest,
  ManifestParam,
  ManifestSlot,
  ManifestWarning,
  coerceManifest,
  parseManifest,
} from './manifest';
import { inferManifest } from './manifestInference';

export interface BlockDefinition {
  /** `core.button` | `fragment:partials/xxxl/footer` | `native.html` */
  type: string;
  /** Fragment key to include/embed; null for TypeScript primitives. */
  fragmentKey: string | null;
  /**
   * The partner's label for that fragment. Null for the primitives, for `core/*`
   * (which is named by its translated `labelKey`) and for rows with no name —
   * their key is the label instead. See `resolveBlockDisplayLabel`.
   */
  fragmentName?: string | null;
  /** Translation key for the palette/inspector title. */
  labelKey: string | null;
  params: ManifestParam[];
  slots: ManifestSlot[];
  /** Declared `@inline`; see COMPILE_WARNING.INLINE_UNSUPPORTED. */
  inline: boolean;
  /**
   * True when the component declared a `{# @param … #}` manifest.
   *
   * Also decides how `compile()` orders arguments: a declared component emits its
   * manifest's params, an undeclared one emits **whatever props the block holds**.
   * Inferred definitions stay `declared: false` on purpose — inference is a guess,
   * and emitting only the guessed names would silently drop a prop it missed.
   */
  declared: boolean;
  /** Params were inferred from the source rather than declared. See `inferred`. */
  inferred?: boolean;
  /**
   * Whether the component renders its own `data-cvn-id`, which is what the canvas
   * hit-tests against.
   *
   * Declaring a `{# @component #}` manifest **is** the promise to render it — every
   * `core/*` component does, from the injected `_cvn_id` param. A partner's own
   * partial declares nothing and has no reason to, and the raw-HTML primitive
   * deliberately injects nothing into arbitrary markup.
   *
   * Structural rather than a `grep` of the source: a text test would silently flip the
   * moment a partner mentioned `_cvn_id` in a comment, and selection would break again
   * with nothing to point at. Blocks that answer `false` get comment markers from the
   * compiler instead, which is what makes them selectable and draggable.
   */
  rendersOwnMarker: boolean;
}

export interface Registry {
  definitions: Record<string, BlockDefinition>;
  /** Manifest problems, keyed by fragment key, for the editor to surface. */
  warnings: Record<string, ManifestWarning[]>;
}

/**
 * Fallback block type for a fragment whose manifest omits `@component`. Keeps
 * partner fragments addressable before anyone declares a manifest for them
 * (the inference pass lands in Sprint 6).
 */
export function fragmentBlockType(fragmentKey: string): string {
  return `fragment:${fragmentKey}`;
}

/** The two primitives with TypeScript emitters rather than a Twig source. */
const PRIMITIVES: BlockDefinition[] = [
  {
    type: NATIVE_BLOCK.HTML,
    fragmentKey: null,
    labelKey: 'email_component_labels.custom_html.title',
    // `code`, not `text`: this holds arbitrary HTML *and* Twig, so it needs a code
    // editor. Declared as a kind rather than special-cased in the inspector, so a
    // component could declare one too.
    params: [{ name: 'html', kind: 'code', labelKey: null, required: false }],
    slots: [],
    inline: true,
    declared: true,
    // Arbitrary markup has no safe place to inject an attribute; the compiler
    // brackets it with comment markers instead.
    rendersOwnMarker: false,
  },
];

function definitionFromManifest(
  fragmentKey: string,
  manifest: ComponentManifest,
  fragmentName: string | null,
): BlockDefinition {
  return {
    type: manifest.type ?? fragmentBlockType(fragmentKey),
    fragmentKey,
    fragmentName,
    labelKey: manifest.labelKey,
    params: manifest.params,
    slots: manifest.slots,
    inline: manifest.inline,
    declared: true,
    rendersOwnMarker: true,
  };
}

/**
 * Builds the registry from the fragment catalog. Layout fragments are excluded:
 * a layout wraps the template (`layout_key`), it is not a block that can be
 * placed inside one.
 *
 * A fragment with no manifest still gets a definition — it can be placed and
 * included, just without a prop schema — so an undeclared partner fragment is
 * degraded, never invisible.
 */
export function buildRegistry(
  fragments: Record<string, string> = {},
  fragmentTypes: Record<string, string> = {},
  /**
   * Fragment key → the server-materialized manifest (the row's `manifest`
   * field). Preferred over parsing the comment out of the source; null/absent
   * rows (saved before the column, or with no manifest at all) fall back to
   * `parseManifest` so nothing depends on the backfill having run.
   */
  manifests: Record<string, unknown> = {},
  /**
   * Fragment key → the partner's label for it. A partner block's key is opaque,
   * so without this the palette would list hex strings.
   */
  names: Record<string, string | null> = {},
): Registry {
  const definitions: Record<string, BlockDefinition> = {};
  const warnings: Record<string, ManifestWarning[]> = {};

  for (const primitive of PRIMITIVES) {
    definitions[primitive.type] = primitive;
  }

  for (const [fragmentKey, source] of Object.entries(fragments)) {
    if (fragmentTypes[fragmentKey] === 'layout') continue;

    const manifest =
      coerceManifest(manifests[fragmentKey]) ?? parseManifest(source);
    if (manifest === null) {
      /**
       * No manifest — so read the props out of the source instead. A partner's own
       * `partials/xxxl/*` predate manifests entirely, and they are what makes a
       * partner's email theirs, so "no manifest" must mean *degraded*, not absent.
       */
      const inferred = inferManifest(fragmentKey, source);
      definitions[inferred.type as string] = {
        type: inferred.type as string,
        fragmentKey,
        fragmentName: names[fragmentKey] ?? null,
        labelKey: null,
        params: inferred.params,
        slots: [],
        inline: false,
        declared: false,
        inferred: true,
        rendersOwnMarker: false,
      };
      continue;
    }

    const definition = definitionFromManifest(
      fragmentKey,
      manifest,
      names[fragmentKey] ?? null,
    );
    definitions[definition.type] = definition;
    if (manifest.warnings.length > 0) {
      warnings[fragmentKey] = manifest.warnings;
    }
  }

  return { definitions, warnings };
}

export const EMPTY_REGISTRY: Registry = { definitions: {}, warnings: {} };
